import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Button,
    ExpandableSection,
    DataTable,
    DataTableCell,
    DataTableColumn,
    ButtonGroup
} from "@salesforce/design-system-react";
import { ToastState } from "../../../../utils/types/toast";
import { CONSTANT_ACCOUNT } from "../../../../constants/accountManagement";
import { CustomHeightPageSpinner } from "../../../../utils/components/spinner/page_spinner_overlay";
import { ScpDriftInfo } from "../../../../utils/types/scpDrift";
import { DriftDetectionApiServiceFactory } from "../../../../services/drift_detection/factory";

type SCPDriftProps = {
    isActive: boolean;
    setActiveTab: (tab: string) => void;
    setActiveSubTab: (tabIndex: number) => void;
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
};

const SCPDrift: React.FC<SCPDriftProps> = ({ isActive, setToast }) => {
    const [data, setData] = useState<ScpDriftInfo[]>([]);
    const [filteredData, setFilteredData] = useState<ScpDriftInfo[]>([]);
    const [isScpListLoading, setIsScpListLoading] = useState<boolean>(false);
    const [isGlossaryOpen, setIsGlossaryOpen] = useState<boolean>(false);

    // Search filters
    const [scpNameSearchTerm, setScpNameSearchTerm] = useState<string>('');
    const [mpaIdSearchTerm, setMpaIdSearchTerm] = useState<string>('');
    const [isFalconFilter, setIsFalconFilter] = useState<string>('all'); // Default to 'all' to show all records

    // Pagination controls
    const [currentPage, setCurrentPage] = useState<number>(1);
    const rowsPerPage: number = 50;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentPageData = filteredData.slice(startIndex, endIndex);

    const fetchScpDrift = useCallback(async () => {
        setIsScpListLoading(true);
        try {
            const service = DriftDetectionApiServiceFactory.getScpDriftService();
            // Auto-set timestamps to last 7 days (matching stackset drift pattern)
            const filters = {
                scp_name: scpNameSearchTerm || undefined,
                mpa_id: mpaIdSearchTerm || undefined,
                from_timestamp: Math.round((Date.now() - 7 * 24 * 60 * 60 * 1000) / 1000), // 7 days ago
                to_timestamp: Math.round(Date.now() / 1000) // Current time
            };
            const response = await service.getScpDrift(filters);
            setData(response);
            setToast({
                isVisible: true,
                variant: "success",
                message: {
                    heading: "Success",
                    details: `Successfully loaded ${response.length} SCP drift records`
                }
            });
        } catch (error) {
            console.error("Error fetching SCP drift data:", error);
            setToast({
                isVisible: true,
                variant: "error",
                message: {
                    heading: "Error",
                    details: "Failed to load SCP drift data"
                }
            });
            setData([]);
        } finally {
            setIsScpListLoading(false);
        }
    }, [setToast, scpNameSearchTerm, mpaIdSearchTerm]);

    const clearFilters = useCallback(() => {
        setScpNameSearchTerm('');
        setMpaIdSearchTerm('');
        setIsFalconFilter('all');
        setIsGlossaryOpen(false);
    }, []);

    const filterScps = useCallback(() => {
        let tempFilteredData = data;

        if (scpNameSearchTerm) {
            tempFilteredData = tempFilteredData.filter(scp =>
                scp.scp_name.toLowerCase().includes(scpNameSearchTerm.toLowerCase())
            );
        }

        if (mpaIdSearchTerm) {
            tempFilteredData = tempFilteredData.filter(scp =>
                scp.mpa_id.includes(mpaIdSearchTerm)
            );
        }

        if (isFalconFilter && isFalconFilter !== 'all') {
            const filterValue = isFalconFilter === 'true';
            tempFilteredData = tempFilteredData.filter(scp => scp.is_falcon === filterValue);
        }

        // Sort by drift_check_timestamp in reverse chronological order
        tempFilteredData.sort((a, b) => (a.drift_check_timestamp > b.drift_check_timestamp ? -1 : 1));

        setFilteredData(tempFilteredData);
        setCurrentPage(1);
    }, [data, scpNameSearchTerm, mpaIdSearchTerm, isFalconFilter]);

    useEffect(() => {
        if (isActive) {
            clearFilters();
            fetchScpDrift();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    // Client-side filtering for mpa_id and is_falcon (not supported by backend)
    useEffect(() => {
        filterScps();
    }, [filterScps]);

    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, Math.ceil(filteredData.length / rowsPerPage)));
    };

    const handleRefreshClick = () => {
        fetchScpDrift();
    };

    const onGlossaryClick = () => {
        setIsGlossaryOpen(!isGlossaryOpen);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        if (event.target.id === "searchScpNameInput") {
            setScpNameSearchTerm(searchTerm);
        }
        if (event.target.id === "searchMpaIdInput") {
            setMpaIdSearchTerm(searchTerm);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Trigger API fetch on Enter key press (like Refresh button)
        if (event.key === 'Enter') {
            event.preventDefault();
            handleRefreshClick();
        }
    };

    const handleIsFalconChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setIsFalconFilter(event.target.value);
    };

    const handleResetFiltersClick = () => {
        clearFilters();
    };

    const handleDownloadClick = () => {
        // Convert JSON to CSV
        const convertToCSV = (jsonArray: ScpDriftInfo[]): string => {
            if (!jsonArray || jsonArray.length === 0) return '';
            const headers = Object.keys(jsonArray[0]).join(',');
            const rows = jsonArray.map(item => {
                return Object.keys(item)
                    .map(key => {
                        const value = item[key as keyof ScpDriftInfo];
                        return value !== undefined ? `"${value}"` : '';
                    })
                    .join(',');
            });
            return [headers].concat(rows).join('\n');
        };

        const csvString = convertToCSV(filteredData);
        const blob = new Blob([csvString], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = "scp_drift_detection.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    type CustomCellProps = {
        children?: React.ReactNode;
        item?: ScpDriftInfo;
    };

    const CustomIsFalconCell: React.FC<CustomCellProps> = ({ children, item, ...props }) => {
        // DataTable passes the row item as 'item' prop
        if (!item || item.is_falcon === undefined || item.is_falcon === null) {
            return <DataTableCell {...props}>-</DataTableCell>;
        }
        // Display true or false as text
        return (
            <DataTableCell {...props}>
                {String(item.is_falcon)}
            </DataTableCell>
        );
    };
    CustomIsFalconCell.displayName = DataTableCell.displayName;

    // Helper function to format timestamp to readable date
    const formatTimestamp = (timestamp: number): string => {
        if (!timestamp || isNaN(timestamp)) {
            return '-';
        }
        // Timestamp is in seconds (Unix timestamp), convert to milliseconds
        const date = new Date(timestamp * 1000);
        if (isNaN(date.getTime())) {
            return '-';
        }
        return date.toLocaleString();
    };

    const CustomTimestampCell: React.FC<CustomCellProps> = ({ children, item, ...props }) => {
        // DataTable passes the row item as 'item' prop
        if (!item || !item.drift_check_timestamp) {
            return <DataTableCell {...props}>-</DataTableCell>;
        }
        return (
            <DataTableCell {...props}>
                {formatTimestamp(item.drift_check_timestamp)}
            </DataTableCell>
        );
    };
    CustomTimestampCell.displayName = DataTableCell.displayName;

    return (
        <div className="slds-is-relative slds-size_1-of-1" style={{ minHeight: "80vh" }}>
            {/* Section 1: Search and Filter */}
            <Card
                heading={
                    <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center">
                        <span>{CONSTANT_ACCOUNT.SEARCH_AND_FILTER}</span>
                        <ButtonGroup>
                            <Button
                                iconCategory="utility"
                                label={<>&nbsp; Reset</>}
                                iconName="skip_back"
                                title="Reset Filters"
                                iconSize="medium"
                                onClick={handleResetFiltersClick}
                                disabled={isScpListLoading}
                                variant={"neutral"}
                                className="slds-button_icon slds-button_small slds-text-body_regular"
                            />
                        </ButtonGroup>
                    </div>
                }
                className=""
                bodyClassName="slds-p-right_medium slds-p-left_medium slds-p-bottom_xx-small"
            >
                <div className="slds-grid slds-gutters slds-m-bottom--x-small slds-m-left--x-small slds-grid_vertical-align-center">
                    <div className="slds-col slds-size_1-of-3">
                        <label className="slds-form-element__label" htmlFor="searchScpNameInput">SCP Name:</label>
                        <input
                            id="searchScpNameInput"
                            type="text"
                            title="SCP Name"
                            className="slds-input"
                            placeholder="SCP Name"
                            value={scpNameSearchTerm}
                            onChange={(e) => handleSearchChange(e)}
                            onKeyDown={handleKeyDown}
                            disabled={isScpListLoading}
                        />
                    </div>
                    <div className="slds-col slds-size_1-of-3">
                        <label className="slds-form-element__label" htmlFor="searchMpaIdInput">MPA ID:</label>
                        <input
                            id="searchMpaIdInput"
                            type="text"
                            title="MPA ID"
                            className="slds-input"
                            placeholder="MPA ID"
                            value={mpaIdSearchTerm}
                            onChange={(e) => handleSearchChange(e)}
                            onKeyDown={handleKeyDown}
                            disabled={isScpListLoading}
                        />
                    </div>
                    <div className="slds-col slds-size_1-of-3">
                        <label className="slds-form-element__label" htmlFor="isFalconSelect">Is Falcon:</label>
                        <select
                            className="slds-select"
                            value={isFalconFilter}
                            onChange={handleIsFalconChange}
                            disabled={isScpListLoading}
                        >
                            <option value="all">All</option>
                            <option value="true">True</option>
                            <option value="false">False</option>
                        </select>
                    </div>
                </div>
            </Card>

            {/* Section 2: Glossary */}
            <div className="slds-m-top--x-small slds-color__background_gray-3">
                <ExpandableSection
                    title='Glossary'
                    isOpen={isGlossaryOpen}
                    onToggleOpen={onGlossaryClick}
                >
                    <ul className="slds-list_dotted">
                        <li>
                            <strong>Data Range:</strong> The data displayed shows SCP drift detection results for the last 7 days.
                        </li>
                        <li>
                            <strong>SCP Name:</strong> The name of the Service Control Policy that defines permissions and restrictions for AWS accounts.
                        </li>
                        <li>
                            <strong>MPA ID:</strong> Master Payer Account ID - the billing account that manages costs and billing for associated AWS accounts.
                        </li>
                        <li>
                            <strong>Drift Check Time:</strong> The timestamp when the last drift detection was performed for this SCP.
                        </li>
                        <li>
                            <strong>Is Falcon:</strong> Indicates if targetId is belongs to Falcon or not.
                        </li>
                        <li>
                            <strong>Target ID:</strong> The unique identifier of the AWS account or organizational unit.
                        </li>
                        <li>
                            <strong>Target Path:</strong> The hierarchical path in the AWS organization structure.
                        </li>
                        <li>
                            <strong>Target Type:</strong> The type of target.
                        </li>
                        <li>
                            <strong>SCP Drift:</strong> SCP drift means a SCP rule is available in source of truth (dynamoDB), but the SCP is not attached in the target.
                        </li>
                    </ul>
                </ExpandableSection>
            </div>

            {/* Section 3: SCP Drift Distribution */}
            {isScpListLoading ? (
                <CustomHeightPageSpinner size="medium" minHeight="30vh"/>
            ) : (
                <>
                    {/* Distribution Chart */}
                    <div className="slds-grid slds-wrap">
                        <div className="slds-col slds-size_1-of-1 slds-m-right_small">
                            <ExpandableSection title="SCP Drift Distribution" defaultOpen>
                                <div className="slds-grid slds-wrap slds-gutters_small">
                                    <div className="slds-col slds-size_1-of-2">
                                        <Card heading="Total SCPs" className="slds-text-align_center">
                                            <p className="slds-text-heading_large">{data.length}</p>
                                        </Card>
                                    </div>
                                    <div className="slds-col slds-size_1-of-2">
                                        <Card heading="Falcon SCPs" className="slds-text-align_center">
                                            <p className="slds-text-heading_large slds-text-color_success">
                                                {data.filter(scp => scp.is_falcon).length}
                                            </p>
                                        </Card>
                                    </div>
                                </div>
                            </ExpandableSection>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="slds-grid slds-grid_vertical-align-center slds-m-bottom_small">
                        <div className="slds-form-element slds-m-right--large">
                            <div className="slds-form-element__control slds-col">
                                <ButtonGroup>
                                    <Button
                                        iconCategory="utility"
                                        iconName="refresh"
                                        title="Refresh"
                                        iconSize="medium"
                                        onClick={handleRefreshClick}
                                        disabled={isScpListLoading}
                                        variant={"neutral"}
                                        className="slds-button_icon slds-button_small"
                                    />
                                    <Button
                                        iconCategory="utility"
                                        iconName="download"
                                        title="Download CSV"
                                        iconSize="medium"
                                        onClick={handleDownloadClick}
                                        disabled={isScpListLoading}
                                        variant={"neutral"}
                                        className="slds-button_icon slds-button_small"
                                    />
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>

                    {/* Top Pagination */}
                    <div className="slds-grid slds-grid_vertical-align-center slds-m-bottom_small slds-box slds-color__background_gray-3">
                        <span className="slds-col slds-text-color_success slds-text-title_bold">
                            Showing {filteredData.length > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, filteredData.length)} of {filteredData.length}
                        </span>
                        {!isScpListLoading && (
                            <div className="slds-grid">
                                <div className="slds-col slds-size_1-of-2 slds-align-middle">
                                    <span>
                                        Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage) || 1}
                                    </span>
                                </div>
                                <div className="slds-col slds-size_1-of-2">
                                    <ButtonGroup className="slds-m-left--xxx-small">
                                        <Button label="<" onClick={handlePreviousPage} disabled={currentPage === 1}/>
                                        <Button label=">" onClick={handleNextPage} disabled={currentPage >= Math.ceil(filteredData.length / rowsPerPage)}/>
                                    </ButtonGroup>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Data Table */}
                    <div className="slds-col slds-size_8-of-8 slds-wrap slds-m-top_small" style={{width: '99%'}}>
                        <DataTable
                            items={currentPageData}
                            id="scp_drift_table"
                            hasCellActions={false}
                            striped={true}
                            resizeBehavior="auto"
                        >
                            <DataTableColumn label="SCP Name" property="scp_name" />
                            <DataTableColumn label="MPA ID" property="mpa_id" />
                            <DataTableColumn label="Drift Check Time" property="drift_check_timestamp">
                                <CustomTimestampCell />
                            </DataTableColumn>
                            <DataTableColumn label="Is Falcon" property="is_falcon">
                                <CustomIsFalconCell />
                            </DataTableColumn>
                            <DataTableColumn label="Target ID" property="target_id" />
                            <DataTableColumn label="Target Path" property="target_path" />
                            <DataTableColumn label="Target Type" property="target_type" />
                        </DataTable>
                    </div>
                </>
            )}
        </div>
    );
};

export default SCPDrift;